import type { ShareOptionInstructions } from "./share-bottom-sheet";

/** Platform IDs for consistent referencing */
export const enum SharePlatformId {
  INSTAGRAM = "instagram",
  TWITTER = "twitter",
  FACEBOOK = "facebook",
  TELEGRAM = "telegram",
  LINKEDIN = "linkedin",
  TIKTOK = "tiktok",
  QUORA = "quora",
  MEDIUM = "medium",
  LINKTREE = "linktree",
  YOUTUBE = "youtube",
  SUBSTACK = 'substack'
}

/** Helper to create async redirect handler */
function createRedirectHandler(
  platform: string,
  profileUrl?: string,
  handle?: string
) {
  return async () => {
    // Dynamic import to avoid SSR issues
    const utils = await import("@artha/utils");
    
    const extractedHandle = handle || (profileUrl ? utils.extractHandleFromProfileUrl(profileUrl) : undefined);
    
    // Open platform URL with smart deep linking
    await utils.openPlatformUrl(platform as any, profileUrl, extractedHandle);
  };
}

/** Factory function to create platform instructions with dynamic link and profile URL */
export type PlatformInstructionsFactory = (
  shareLink: string, 
  profileUrl?: string
) => ShareOptionInstructions;

/**
 * Extract domain from share link for display in instructions
 * e.g., "artha.link/username" -> "artha.link"
 */
function extractDomain(shareLink: string): string {
  // Remove protocol if present
  const withoutProtocol = shareLink.replace(/^https?:\/\//, "");
  // Get domain part (before first /)
  const domain = withoutProtocol.split("/")[0];
  return domain || "artha.link";
}

/** Platform instruction factories - call with shareLink to get instructions */
export const PLATFORM_INSTRUCTIONS: Record<SharePlatformId, PlatformInstructionsFactory> = {
  [SharePlatformId.INSTAGRAM]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "Instagram",
      gradient: "linear-gradient(135deg, #F6DDFF 0%, #FFE4EC 100%)",
      steps: [
        "Navigate to your profile page on the Instagram app",
        <>Hit <span className="font-semibold">"Edit Profile"</span></>,
        <>Paste your {domain} URL into the <span className="font-semibold">"Website"</span> field and add website name like "Job Opportunities"</>,
        "Save your changes",
        "Click on the link in your bio to test it is working",
      ],
      helperText: (
        <>
          Sometimes a <span className="font-semibold">"Links field"</span> replaces the{" "}
          <span className="font-semibold">"Website"</span> field. Use whichever field is available.
        </>
      ),
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my Instagram" : "Open Instagram",
      onActionClick: createRedirectHandler('instagram', profileUrl),
    };
  },

  [SharePlatformId.TWITTER]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "Twitter (X)",
      gradient: "linear-gradient(135deg, #E8F4FD 0%, #F0F0F0 100%)",
      steps: [
        <>Open your Twitter (X) profile and tap <span className="font-semibold">"Edit profile"</span></>,
        <>Paste your {domain} link in the <span className="font-semibold">"Website"</span> field</>,
        "Save your changes to make the link clickable",
        <>Add a short bio line like <span className="font-semibold">"Sharing job openings → {shareLink}"</span></>,
        <>Optional: Pin a tweet mentioning your {domain} link for extra visibility</>,
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my Twitter" : "Open Twitter",
      onActionClick: createRedirectHandler('twitter', profileUrl),
    };
  },

  [SharePlatformId.FACEBOOK]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "Facebook",
      gradient: "linear-gradient(135deg, #E7F0FF 0%, #F0F4FF 100%)",
      steps: [
        <>Open your Facebook profile and click <span className="font-semibold">"Edit profile"</span></>,
        <>Add {domain} in the Bio section with a short explanation</>,
        <>Go to <span className="font-semibold">"About"</span> section → <span className="font-semibold">"Contact and basic info"</span></>,
        <>Add your {domain} link under <span className="font-semibold">"Website"</span> or <span className="font-semibold">"Links"</span></>,
        "Keep the message simple so visitors know why to click",
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my Facebook" : "Open Facebook",
      onActionClick: createRedirectHandler('facebook', profileUrl),
    };
  },

  [SharePlatformId.TELEGRAM]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "Telegram",
      gradient: "linear-gradient(135deg, #E3F2FD 0%, #E8F5E9 100%)",
      steps: [
        "Open your Telegram channel and tap the channel name at the top",
        <>Select <span className="font-semibold">"Edit"</span> to open channel settings</>,
        <>Paste your {domain} link in the channel description</>,
        <>Add one short line like <span className="font-semibold">"Job opportunities shared here → {shareLink}"</span></>,
        <>Optional: Pin a message with {domain} link for new members</>,
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my Telegram" : "Open Telegram",
      onActionClick: createRedirectHandler('telegram', profileUrl),
    };
  },

  [SharePlatformId.LINKEDIN]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "LinkedIn",
      gradient: "linear-gradient(135deg, #E8F4FC 0%, #EDF7FF 100%)",
      steps: [
        <>Open your LinkedIn profile and go to the <span className="font-semibold">"About"</span> section</>,
        <>Click <span className="font-semibold">"Edit"</span> and mention you share job opportunities</>,
        <>Add {domain} naturally with context like <span className="font-semibold">"Job opportunities → {shareLink}"</span></>,
        "Keep the tone professional but friendly",
        "Update your bio when your focus or niche changes",
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my LinkedIn" : "Open LinkedIn",
      onActionClick: createRedirectHandler('linkedin', profileUrl),
    };
  },

  [SharePlatformId.TIKTOK]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "TikTok",
      gradient: "linear-gradient(135deg, #FFE4EC 0%, #E8F4FD 100%)",
      steps: [
        "Open the TikTok app and go to your profile",
        <>Tap <span className="font-semibold">"Edit profile"</span></>,
        <>Paste your {domain} link in the <span className="font-semibold">"Website"</span> field and save</>,
        <>Add a simple bio line like <span className="font-semibold">"Job opportunities here → {shareLink}"</span></>,
        <>Mention <span className="font-semibold">"link in bio"</span> in your videos when relevant</>,
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my TikTok" : "Open TikTok",
      onActionClick: createRedirectHandler('tiktok', profileUrl),
    };
  },

  [SharePlatformId.QUORA]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "Quora",
      gradient: "linear-gradient(135deg, #FFEBEE 0%, #FFF3E0 100%)",
      steps: [
        "Open your Quora profile and edit your bio description",
        <>Add one clear line like <span className="font-semibold">"I share job opportunities here → {shareLink}"</span></>,
        <>If you have a Space, add {domain} link to the Space description</>,
        <>Share {domain} link only when answering job or career-related questions</>,
        "Avoid dropping the link without context or in unrelated answers",
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my Quora" : "Open Quora",
      onActionClick: createRedirectHandler('quora', profileUrl),
    };
  },

  [SharePlatformId.MEDIUM]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "Medium",
      gradient: "linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)",
      steps: [
        <>Go to your Medium profile and click <span className="font-semibold">"Edit profile"</span></>,
        <>In the bio section, add {domain} link with a simple line</>,
        <>Use examples like <span className="font-semibold">"Sharing job opportunities here → {shareLink}"</span></>,
        "Avoid adding multiple links—keep it clean",
        <>Optional: Mention {domain} link at the end of relevant articles</>,
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my Medium" : "Open Medium",
      onActionClick: createRedirectHandler('medium', profileUrl),
    };
  },

  [SharePlatformId.LINKTREE]: (shareLink, profileUrl) => {
    const domain = extractDomain(shareLink);
    return {
      title: "Linktree / Beacons / Bio.fm",
      gradient: "linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)",
      steps: [
        "Open your bio-link tool dashboard and add a new link",
        <>Paste your {domain} link and name it clearly (e.g., <span className="font-semibold">"Jobs"</span> or <span className="font-semibold">"Find Jobs"</span>)</>,
        "Place it at the top 1–2 positions for maximum visibility",
        "Use simple labels—avoid long or unclear text",
        <>Reorder links to keep {domain} link easy to find</>,
      ],
      linkValue: shareLink,
      actionLabel: profileUrl ? "Go to my Linktree" : "Open Linktree",
      onActionClick: createRedirectHandler('linktree', profileUrl),
    };
  },

  [SharePlatformId.YOUTUBE]: (shareLink, profileUrl) => ({
    title: "YouTube",
    gradient: "linear-gradient(135deg, #FFEBEE 0%, #FCE4EC 100%)",
    steps: [
      <>Go to YouTube Studio and open <span className="font-semibold">"Customization"</span> → <span className="font-semibold">"Basic info"</span></>,
      <>Scroll to the <span className="font-semibold">"Links"</span> section and click <span className="font-semibold">"Add Link"</span></>,
      <>Paste your link and add a title like <span className="font-semibold">"Job Opportunities"</span></>,
      "Save changes to make it appear on your channel page",
      <>Mention <span className="font-semibold">"link in description"</span> or <span className="font-semibold">"check my channel"</span> in your videos</>,
    ],
    linkValue: shareLink,
    actionLabel: profileUrl ? "Go to my YouTube" : "Open YouTube",
    onActionClick: createRedirectHandler('youtube', profileUrl),
  }),
   [SharePlatformId.SUBSTACK]: (shareLink, profileUrl) => ({
    title: "Substack",
    gradient: "linear-gradient(135deg, #FFEBEE 0%, #FCE4EC 100%)",
    steps: [
     <>Open <span className="font-semibold">Substack</span> and go to <span className="font-semibold">"Settings"</span> (from your publication dashboard)</>,
<>Click <span className="font-semibold">"Publication details"</span> (sometimes shown as <span className="font-semibold">"Basics"</span> or <span className="font-semibold">"Publication settings"</span>)</>,
<>Scroll to the <span className="font-semibold">"Social links"</span> section</>,
<>Click <span className="font-semibold">"Add social link"</span> (or <span className="font-semibold">"Add link"</span>) and paste your URL</>,
<>Set the label/title to something like <span className="font-semibold">"Job Opportunities"</span> (or <span className="font-semibold">"Careers"</span>)</>,
"Save changes so the link appears on your publication page",
<>In posts, add a CTA line like <span className="font-semibold">"Link in my Substack page"</span> or <span className="font-semibold">"Open the publication and click ‘Job Opportunities’"</span></>,

    ],
    linkValue: shareLink,
    actionLabel: profileUrl ? "Go to my Substack" : "Open Substack",
    onActionClick: createRedirectHandler('Substack', profileUrl),
  }),
};

/** Get instructions for a platform by ID */
export function getPlatformInstructions(
  platformId: string,
  shareLink: string,
  profileUrl?: string
): ShareOptionInstructions | undefined {
  const factory = PLATFORM_INSTRUCTIONS[platformId as SharePlatformId];
  return factory?.(shareLink, profileUrl);
}

/** Get all platform instructions for a given share link */
export function getAllPlatformInstructions(
  shareLink: string,
  profileUrls?: Record<string, string>
): Record<SharePlatformId, ShareOptionInstructions> {
  const result = {} as Record<SharePlatformId, ShareOptionInstructions>;
  for (const [id, factory] of Object.entries(PLATFORM_INSTRUCTIONS)) {
    result[id as SharePlatformId] = factory(shareLink, profileUrls?.[id]);
  }
  return result;
}
